import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, X, FileImage, AlertCircle, CheckCircle, Camera, ArrowLeft, Download, SwitchCamera, ZoomIn, ZoomOut, Maximize2, Mail } from 'lucide-react';
import { apiService } from '../services/api';
import GuidelinePanel from './GuidelinePanel';
import { generateScanAnalysisPDF } from './PDFGenerator';
import TeethFactDisplay from './TeethFactDisplay';
import UserInfoModal from './UserInfoModal';

interface ScanAnalysisPageProps {
  onBack?: () => void;
  onClose?: () => void;
}

const ScanAnalysisPage: React.FC<ScanAnalysisPageProps> = ({ onBack, onClose }) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [userInfo, setUserInfo] = useState<{ fullName: string; email: string } | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [maxZoom, setMaxZoom] = useState(3);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
      setError(null);
      
      // Create previews
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviews(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
      setError(null);
      
      // Create previews
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviews(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  }, []);

  const startCamera = async (mode?: 'user' | 'environment') => {
    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Camera access is not supported in your browser. Please try a different browser.');
        return;
      }

      // Stop existing stream if switching camera
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const selectedMode = mode || facingMode;
      if (mode) {
        setFacingMode(mode);
      }

      // Request camera permissions explicitly
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: { ideal: selectedMode },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false 
      });
      
      setStream(mediaStream);
      setShowCamera(true);
      
      // Check if zoom is supported
      const videoTrack = mediaStream.getVideoTracks()[0];
      const capabilities = videoTrack.getCapabilities ? videoTrack.getCapabilities() : null;
      
      if (capabilities && 'zoom' in capabilities) {
        const zoomCapability = (capabilities as any).zoom;
        if (zoomCapability && typeof zoomCapability === 'object') {
          setMaxZoom(zoomCapability.max || 3);
          setZoomLevel(zoomCapability.min || 1);
        }
      }
      
      // Wait for component to render before setting video source
      setTimeout(() => {
        if (videoRef.current && mediaStream) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(e => {
            console.error('Video play error:', e);
          });
        }
      }, 100);
      
      setError(null);
    } catch (err: any) {
      console.error('Camera error:', err);
      
      // Provide specific error messages
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera permission denied. Please allow camera access in your browser settings and try again.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera found. Please connect a camera and try again.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('Camera is already in use by another application. Please close other apps and try again.');
      } else {
        setError(`Unable to access camera: ${err.message || 'Unknown error'}`);
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
    setZoomLevel(1);
  };

  const updateZoom = (newZoom: number) => {
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      
      // Check if the track supports zoom
      if (videoTrack && 'applyConstraints' in videoTrack) {
        const constraints = {
          advanced: [{
            zoom: newZoom
          }]
        };
        
        videoTrack.applyConstraints(constraints as any)
          .then(() => {
            setZoomLevel(newZoom);
          })
          .catch((error: any) => {
            console.error('Failed to apply zoom:', error);
          });
      }
    }
  };

  const handleZoomIn = () => {
    const newZoom = Math.min(zoomLevel * 1.5, maxZoom);
    updateZoom(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel / 1.5, 1);
    updateZoom(newZoom);
  };

  const handleZoomReset = () => {
    updateZoom(1);
  };

  const handleVideoClick = (e: React.MouseEvent<HTMLVideoElement>) => {
    // Toggle between zoomed and normal view on click
    if (zoomLevel > 1) {
      handleZoomReset();
    } else {
      // Zoom to 2x on click
      updateZoom(Math.min(2, maxZoom));
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      if (context) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Mirror the image if using front camera
        if (facingMode === 'user') {
          context.translate(canvas.width, 0);
          context.scale(-1, 1);
        }
        
        context.drawImage(video, 0, 0);
        
        canvas.toBlob(blob => {
          if (blob) {
            const file = new File([blob], `scan-${Date.now()}.jpg`, { type: 'image/jpeg' });
            setSelectedFiles(prev => [...prev, file]);
            
            const reader = new FileReader();
            reader.onloadend = () => {
              setPreviews(prev => [...prev, reader.result as string]);
            };
            reader.readAsDataURL(blob);
          }
        }, 'image/jpeg', 0.95);
      }
    }
  };

  const removeImage = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleUserInfoSubmit = async (info: { fullName: string; email: string }) => {
    console.log('handleUserInfoSubmit called with:', info);
    console.log('Current analysisResults:', analysisResults);
    
    setUserInfo(info);
    setShowUserInfoModal(false);
    
    // Generate PDF after getting user info
    const allDiagnoses: any[] = [];
    const seenFields = new Set<string>();
    
    analysisResults.forEach(result => {
      result.diagnoses.forEach((diagnosis: any) => {
        if (!seenFields.has(diagnosis.field_name)) {
          seenFields.add(diagnosis.field_name);
          allDiagnoses.push(diagnosis);
        }
      });
    });
    
    // Extract criteria from analysis results
    const criteria = extractCriteriaFromResults(analysisResults);
    console.log('Extracted criteria:', criteria);
    
    // Convert images to base64 for storage
    const imagesBase64: string[] = [];
    for (const file of selectedFiles) {
      try {
        const base64 = await apiService.uploadFile(file);
        imagesBase64.push(base64);
      } catch (error) {
        console.error('Failed to convert file to base64:', error);
      }
    }
    
    // Create comprehensive criteria object with images and analysis results
    const comprehensiveCriteria = {
      ...criteria,
      scan_results: analysisResults,
      images_base64: imagesBase64,
      image_count: selectedFiles.length,
      analysis_date: new Date().toISOString(),
      diagnoses_summary: allDiagnoses
    };
    
    console.log('Comprehensive criteria object:', comprehensiveCriteria);
    console.log('About to call createSmileUser with:', {
      fullName: info.fullName || 'Anonymous',
      email: info.email || `anonymous_${Date.now()}@orthomind.ai`,
      hasInfo: !!(info.fullName || info.email)
    });
    
    // Create smile user with the analysis criteria and images
    try {
      if (info.fullName || info.email) {
        console.log('Calling apiService.createSmileUser...');
        const smileUser = await apiService.createSmileUser({
          fullName: info.fullName || 'Anonymous',
          email: info.email || `anonymous_${Date.now()}@orthomind.ai`,
          notes: `Smart Scan analysis with ${analysisResults.length} images analyzed. ${allDiagnoses.length} findings detected.`,
          criteria: comprehensiveCriteria
        });
        console.log('Smile user created/updated:', smileUser);
      } else {
        console.log('Skipping smile user creation - no fullName or email provided');
      }
    } catch (error) {
      console.error('Failed to create smile user:', error);
      // Continue with PDF generation even if smile user creation fails
    }
    
    // Generate PDF blob for server upload
    const pdfBlob = await generateScanAnalysisPDF(allDiagnoses, previews, selectedFiles, info, true) as Blob;
    
    // Store scan record and upload PDF
    try {
      // First store the scan record
      const scanRecord = await apiService.storeScanRecord({
        fullName: info.fullName,
        email: info.email,
        timestamp: new Date().toISOString(),
        fileNames: selectedFiles.map(f => f.name),
        sessionId: apiService.getCurrentSessionId() || undefined,
        analysisResults: analysisResults
      });
      
      // Then upload the PDF
      const dateStr = new Date().toISOString().split('T')[0];
      const patientName = info.fullName ? `-${info.fullName.replace(/\s+/g, '-')}` : '';
      const filename = `OrthoMind-smart-scan-analysis${patientName}-${dateStr}.pdf`;
      
      const uploadResult = await apiService.uploadPDF(pdfBlob, filename, scanRecord.id);
      console.log('PDF uploaded to server successfully');
      
      // Send email if upload was successful
      if (uploadResult.pdf_url && info.email) {
        try {
          await apiService.sendPDFEmail(info.email, uploadResult.pdf_url, info.fullName);
          console.log('Email sent successfully to:', info.email);
          alert(`PDF report has been sent to ${info.email}`);
        } catch (emailError) {
          console.error('Failed to send email:', emailError);
        }
      }
    } catch (error) {
      console.error('Failed to store scan record or upload PDF:', error);
    }
    
    // Also download to user's device
    await generateScanAnalysisPDF(allDiagnoses, previews, selectedFiles, info);
  };

  const handleAnalyze = async () => {
    if (selectedFiles.length === 0) return;

    // Stop camera if it's running
    if (showCamera) {
      stopCamera();
    }

    setIsAnalyzing(true);
    setError(null);
    setAnalysisResults([]);

    // Add development logging
    const isDev = process.env.NODE_ENV === 'development';
    if (isDev) {
      console.log('🔍 [Smart Scan] Starting analysis with:', {
        fileCount: selectedFiles.length,
        fileNames: selectedFiles.map(f => f.name),
        fileSizes: selectedFiles.map(f => `${(f.size / 1024).toFixed(2)}KB`),
        timestamp: new Date().toISOString()
      });
    }

    try {
      
      if (selectedFiles.length === 1) {
        // Single image
        if (isDev) console.log('📤 [Smart Scan] Uploading single image...');
        const imageBase64 = await apiService.uploadFile(selectedFiles[0]);
        if (isDev) console.log('✅ [Smart Scan] Image converted to base64, size:', imageBase64.length);
        
        if (isDev) console.log('🔄 [Smart Scan] Calling analyze API...');
        const analysisResult = await apiService.analyzeImage(imageBase64) as any;
        
        if (isDev) {
          console.log('✅ [Smart Scan] Single image analysis result:', {
            hasResult: !!analysisResult,
            diagnoses: analysisResult?.diagnoses?.length || 0,
            fullResult: analysisResult
          });
        }
        
        // Handle both single image format and potential merged format
        let diagnoses = [];
        if (analysisResult.diagnoses && Array.isArray(analysisResult.diagnoses)) {
          diagnoses = analysisResult.diagnoses;
        }
        
        const results = [{
          fileName: selectedFiles[0].name,
          diagnoses: diagnoses
        }];
        console.log('Setting analysis results:', results);
        setAnalysisResults(results);
      } else {
        // Multiple images - batch analysis
        const imageBase64Array: string[] = [];
        
        if (isDev) console.log('📤 [Smart Scan] Converting multiple images to base64...');
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          if (isDev) console.log(`  📸 Image ${i + 1}/${selectedFiles.length}: ${file.name}`);
          const base64 = await apiService.uploadFile(file);
          imageBase64Array.push(base64);
        }
        
        if (isDev) console.log('🔄 [Smart Scan] Calling analyze API with multiple images...');
        const analysisResult = await apiService.analyzeImage(imageBase64Array) as any;
        
        if (isDev) {
          console.log('✅ [Smart Scan] Multiple images analysis result:', {
            hasResult: !!analysisResult,
            images: analysisResult?.images?.length || 0,
            diagnoses: analysisResult?.diagnoses?.length || 0,
            fullResult: analysisResult
          });
        }
        
        // Check if we have merged results (new format with diagnoses at root)
        if (analysisResult && analysisResult.diagnoses && Array.isArray(analysisResult.diagnoses)) {
          // Group diagnoses by source_image if available
          const imageGroups: { [key: number]: any[] } = {};
          
          analysisResult.diagnoses.forEach((diagnosis: any) => {
            const imageIndex = diagnosis.source_image || 0;
            if (!imageGroups[imageIndex]) {
              imageGroups[imageIndex] = [];
            }
            imageGroups[imageIndex].push(diagnosis);
          });
          
          // Create results for each image
          const results = selectedFiles.map((file, index) => ({
            fileName: file.name,
            diagnoses: imageGroups[index] || []
          }));
          
          // If all diagnoses are in group 0, distribute them to first image
          if (Object.keys(imageGroups).length === 1 && imageGroups[0]) {
            results[0].diagnoses = imageGroups[0];
          }
          
          console.log('Setting analysis results (new format):', results);
          setAnalysisResults(results);
        } else if ('images' in analysisResult && analysisResult.images) {
          // Old format support
          const results = analysisResult.images.map((imageResult: any, index: number) => ({
            fileName: selectedFiles[index]?.name || `Image ${index + 1}`,
            diagnoses: imageResult.diagnoses || []
          }));
          console.log('Setting analysis results (old format):', results);
          setAnalysisResults(results);
        } else {
          console.error('Unexpected analysis result format:', analysisResult);
          setError('Unexpected response format from analysis');
        }
      }
    } catch (err: any) {
      if (isDev) {
        console.error('❌ [Smart Scan] Analysis error:', {
          error: err,
          message: err.message,
          stack: err.stack,
          response: err.response?.data,
          status: err.response?.status
        });
      } else {
        console.error('Analysis error:', err.message);
      }
      setError(err.message || 'Failed to analyze images');
    } finally {
      setIsAnalyzing(false);
      if (isDev) console.log('🏁 [Smart Scan] Analysis complete');
    }
  };

  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatFieldValue = (value: string): string => {
    if (value.toLowerCase() === 'yes') return '✓ Present';
    if (value.toLowerCase() === 'no') return '✗ Not Present';
    return value;
  };

  const getFieldExplanation = (fieldName: string, fieldValue: string): string => {
    const explanations: { [key: string]: { [value: string]: string } } = {
      'Malocclusion type': {
        'Class I': 'Normal bite relationship with properly aligned molars. May still have crowding or spacing issues.',
        'Class II': 'Upper teeth protrude forward relative to lower teeth (overjet). Often called "overbite" by patients.',
        'Class III': 'Lower teeth protrude forward relative to upper teeth (underbite). May require surgical intervention in severe cases.'
      },
      'All permanent teeth present': {
        'Yes': 'Complete adult dentition is present, which provides more treatment options.',
        'No': 'Missing teeth may require special consideration for space management and treatment planning.'
      },
      'Crowding on upper anterior region': {
        'Yes': 'Insufficient space for teeth alignment in the upper front area. May require expansion or extractions.',
        'No': 'Adequate space available in the upper front teeth area.'
      },
      'Crowding on lower anterior region': {
        'Yes': 'Insufficient space for teeth alignment in the lower front area. Common in adult patients.',
        'No': 'Adequate space available in the lower front teeth area.'
      },
      'Maxillary transverse constriction in premolar region': {
        'Yes': 'Narrow upper jaw in the premolar area. May benefit from palatal expansion.',
        'No': 'Normal width relationship between upper and lower jaws.'
      },
      'Spee curve on lower arch': {
        'Yes': 'Pronounced curve in the lower arch. Leveling will require vertical space.',
        'No': 'Relatively flat occlusal plane in the lower arch.'
      },
      'Upper midline aligned with face midline': {
        'Yes': 'Dental midline matches facial midline, which is aesthetically ideal.',
        'No': 'Dental midline deviation present. May require asymmetric mechanics to correct.'
      },
      'Distalize more than 1mm': {
        'Yes': 'Significant backward movement of teeth required. May need TADs or special mechanics.',
        'No': 'Minimal or no distalization needed, simplifying treatment.'
      }
    };

    const fieldKey = Object.keys(explanations).find(key => 
      fieldName.toLowerCase().includes(key.toLowerCase())
    );

    if (fieldKey && explanations[fieldKey][fieldValue]) {
      return explanations[fieldKey][fieldValue];
    }

    // Default explanations for yes/no values
    if (fieldValue.toLowerCase() === 'yes') {
      return 'This finding is present and will be considered in the treatment plan.';
    } else if (fieldValue.toLowerCase() === 'no') {
      return 'This finding is not present, which may simplify treatment.';
    }

    return 'This finding will be evaluated as part of the comprehensive treatment plan.';
  };

  const clearAll = () => {
    setSelectedFiles([]);
    setPreviews([]);
    setAnalysisResults([]);
    setError(null);
    stopCamera();
  };

  // Extract criteria from analysis results
  const extractCriteriaFromResults = (results: any[]): any => {
    const criteria: any = {};
    
    results.forEach(result => {
      result.diagnoses.forEach((diagnosis: any) => {
        const fieldName = diagnosis.field_name.toLowerCase();
        const fieldValue = diagnosis.field_value.toLowerCase();
        
        // Map diagnosis fields to criteria
        if (fieldName.includes('class') && (fieldName.includes('ii') || fieldName.includes('2'))) {
          criteria.malocclusion_type = 'Class II';
        } else if (fieldName.includes('class') && (fieldName.includes('iii') || fieldName.includes('3'))) {
          criteria.malocclusion_type = 'Class III';
        } else if (fieldName.includes('class') && (fieldName.includes('i') || fieldName.includes('1'))) {
          criteria.malocclusion_type = 'Class I';
        }
        
        if (fieldName.includes('crowding')) {
          criteria.crowding = fieldValue === 'yes' ? 'moderate' : 'none';
        }
        
        if (fieldName.includes('overbite')) {
          criteria.overbite = fieldValue === 'yes';
        }
        
        if (fieldName.includes('crossbite')) {
          criteria.crossbite = fieldValue === 'yes';
        }
        
        if (fieldName.includes('spacing')) {
          criteria.spacing = fieldValue === 'yes';
        }
        
        if (fieldName.includes('tmj') || fieldName.includes('temporomandibular')) {
          criteria.tmj_issues = fieldValue === 'yes';
        }
        
        if (fieldName.includes('extraction')) {
          criteria.extraction_needed = fieldValue === 'yes';
        }
        
        if (fieldName.includes('impacted') || fieldName.includes('wisdom')) {
          criteria.impacted_teeth = fieldValue === 'yes';
        }
        
        if (fieldName.includes('open bite') || fieldName.includes('openbite')) {
          criteria.open_bite = fieldValue === 'yes';
        }
        
        // Store all raw diagnoses as well
        const cleanFieldName = fieldName.replace(/_/g, ' ').trim();
        criteria[cleanFieldName] = fieldValue === 'yes' ? true : fieldValue === 'no' ? false : fieldValue;
      });
    });
    
    return criteria;
  };

  // Debug logging
  console.log('Current state - analysisResults:', analysisResults, 'length:', analysisResults.length);

  return (
    <div className="h-full bg-medical-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-medical-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-medical-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-medical-700" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-medical-900">Smart Scan Analysis</h1>
              <p className="text-sm text-medical-600">Upload or capture dental scans for AI analysis</p>
            </div>
          </div>
          {selectedFiles.length > 0 && (
            <div className="flex items-center space-x-3">
              <span className="text-sm text-medical-600">
                {selectedFiles.length} image{selectedFiles.length > 1 ? 's' : ''} selected
              </span>
              <button
                onClick={clearAll}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Clear All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto">
          {/* Upload/Camera Section */}
          {!showCamera && selectedFiles.length === 0 && analysisResults.length === 0 && (
            <>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Upload Area */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="border-2 border-dashed border-medical-300 rounded-lg p-8 text-center hover:border-primary-500 transition-colors bg-white"
              >
                <Upload className="w-12 h-12 text-medical-400 mx-auto mb-4" />
                <p className="text-medical-700 mb-2">Drop dental scans here or</p>
                <label className="medical-button cursor-pointer inline-block mb-3">
                  Choose Files
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
                <p className="text-sm text-medical-500">Supports JPG, PNG, and other image formats</p>
              </div>

              {/* Camera Area */}
              <div className="border-2 border-dashed border-medical-300 rounded-lg p-8 text-center bg-white">
                <Camera className="w-12 h-12 text-medical-400 mx-auto mb-4" />
                <p className="text-medical-700 mb-2">Take a photo with your camera</p>
                <button
                  onClick={() => startCamera()}
                  className="medical-button flex items-center space-x-2 mx-auto"
                >
                  <Camera className="w-4 h-4" />
                  <span>Open Camera</span>
                </button>
                <p className="text-sm text-medical-500 mt-3">Use your device camera to capture scans</p>
              </div>
            </div>
            
            {/* Example Text */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm">
                <strong>💡 Tip:</strong> If you have scans from your 3D intraoral scanner, you can upload multiple images at once.
                For example: upper arch, lower arch, bite registration, and facial photos.
              </p>
            </div>
            
            {/* Image Guidance Section */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-medical-900 mb-4">Photo Guidelines</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Left - Scan Upload Examples */}
                <GuidelinePanel type="scan" />
                
                {/* Center Content - Upload Tips */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h4 className="font-medium text-medical-800 mb-3 text-center">📋 Quick Tips</h4>
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-blue-800 mb-1">For Scan Uploads:</p>
                      <ul className="text-xs text-blue-700 space-y-1">
                        <li>• Export from iTero, 3Shape, or similar</li>
                        <li>• Include multiple views</li>
                        <li>• High resolution screenshots</li>
                      </ul>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-green-800 mb-1">For Camera Photos:</p>
                      <ul className="text-xs text-green-700 space-y-1">
                        <li>• Good lighting is essential</li>
                        <li>• Use retractors if available</li>
                        <li>• Take all 4 views shown</li>
                      </ul>
                    </div>
                    <div className="mt-4 p-3 bg-medical-50 rounded-lg">
                      <p className="text-xs text-medical-700 text-center">
                        <strong>Best results:</strong> Upload 4-6 images from either source
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Right - Camera Photo Examples */}
                <GuidelinePanel type="camera" />
              </div>
            </div>
            </>
          )}

          {/* Camera View */}
          {showCamera && (
            <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
              {/* Camera Guidelines at Top */}
              <GuidelinePanel type="camera" isInCameraView={true} />
              <div className="relative">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  onClick={handleVideoClick}
                  className={`w-full rounded-lg cursor-pointer ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
                  style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                  title="Click to zoom in/out"
                />
                <canvas ref={canvasRef} className="hidden" />
                
                {/* Camera Controls */}
                <div className="absolute top-4 right-4 flex flex-col space-y-2">
                  {/* Camera Switch Button */}
                  <button
                    onClick={() => startCamera(facingMode === 'user' ? 'environment' : 'user')}
                    className="bg-white bg-opacity-80 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition-all"
                    title="Switch camera"
                  >
                    <SwitchCamera className="w-5 h-5 text-medical-700" />
                  </button>
                </div>
                
                {/* Zoom Controls */}
                <div className="absolute bottom-4 right-4 flex flex-col space-y-2">
                  <button
                    onClick={handleZoomIn}
                    className="bg-white bg-opacity-80 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition-all disabled:opacity-50"
                    title="Zoom in"
                    disabled={zoomLevel >= maxZoom}
                  >
                    <ZoomIn className="w-5 h-5 text-medical-700" />
                  </button>
                  
                  <button
                    onClick={handleZoomReset}
                    className="bg-white bg-opacity-80 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition-all"
                    title="Reset zoom"
                  >
                    <Maximize2 className="w-5 h-5 text-medical-700" />
                  </button>
                  
                  <button
                    onClick={handleZoomOut}
                    className="bg-white bg-opacity-80 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition-all disabled:opacity-50"
                    title="Zoom out"
                    disabled={zoomLevel <= 1}
                  >
                    <ZoomOut className="w-5 h-5 text-medical-700" />
                  </button>
                </div>
                
                {/* Zoom Level Indicator */}
                {zoomLevel > 1 && (
                  <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                    {zoomLevel.toFixed(1)}x
                  </div>
                )}
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={capturePhoto}
                  className="medical-button flex items-center space-x-2"
                >
                  <Camera className="w-4 h-4" />
                  <span>Capture Photo</span>
                </button>
                <button
                  onClick={stopCamera}
                  className="medical-button-secondary"
                >
                  Close Camera
                </button>
              </div>
            </div>
          )}

          {/* Preview Grid - Only show if we have images to analyze */}
          {previews.length > 0 && analysisResults.length === 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-medical-900 mb-4">Selected Images</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                {previews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={preview}
                      alt={`Scan ${index + 1}`}
                      className="w-full h-40 object-cover rounded-lg border border-medical-200"
                    />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="absolute bottom-2 left-2 right-2 bg-white bg-opacity-90 rounded px-2 py-1">
                      <p className="text-xs truncate">{selectedFiles[index].name}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-center space-x-4">
                <button
                  onClick={handleAnalyze}
                  disabled={isAnalyzing}
                  className="medical-button flex items-center space-x-2"
                >
                  {isAnalyzing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      <span>Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <FileImage className="w-4 h-4" />
                      <span>Analyze Scans</span>
                    </>
                  )}
                </button>
                
                <label className="medical-button-secondary cursor-pointer">
                  Add More Images
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>

                <button
                  onClick={() => startCamera()}
                  className="medical-button-secondary flex items-center space-x-2"
                >
                  <Camera className="w-4 h-4" />
                  <span>Camera</span>
                </button>
              </div>
            </div>
          )}

          {/* Modern Loading Overlay */}
          {isAnalyzing && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-2xl p-8 max-w-sm w-full mx-4">
                <div className="flex flex-col items-center">
                  {/* Animated Loading Icon */}
                  <div className="relative w-24 h-24 mb-6">
                    <div className="absolute inset-0 border-4 border-medical-200 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-primary-600 rounded-full border-t-transparent animate-spin"></div>
                    <div className="absolute inset-3 border-4 border-medical-300 rounded-full"></div>
                    <div className="absolute inset-3 border-4 border-primary-500 rounded-full border-t-transparent animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1s' }}></div>
                    <div className="absolute inset-6 flex items-center justify-center">
                      <FileImage className="w-8 h-8 text-primary-600" />
                    </div>
                  </div>
                  
                  {/* Loading Text */}
                  <h3 className="text-xl font-semibold text-medical-900 mb-2">Analyzing Your Scans</h3>
                  <p className="text-medical-600 text-center mb-4">
                    Our AI is examining the images for orthodontic features...
                  </p>
                  
                  {/* Progress Indicator */}
                  <div className="w-full bg-medical-100 rounded-full h-2 overflow-hidden">
                    <div className="bg-primary-600 h-full rounded-full animate-pulse" style={{ width: '60%' }}></div>
                  </div>
                  
                  {/* Teeth Facts */}
                  <TeethFactDisplay />
                  
                  {/* Loading Tips */}
                  <div className="mt-4 text-sm text-medical-500 text-center">
                    <p className="animate-pulse">This usually takes 30-60 seconds</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="text-red-800 font-medium">Analysis Error</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Results Area */}
          {analysisResults.length > 0 && (
            <div className="space-y-8">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-green-800">
                  Successfully analyzed {selectedFiles.length} image{selectedFiles.length > 1 ? 's' : ''} - Found {analysisResults.reduce((acc, r) => acc + r.diagnoses.length, 0)} clinical findings
                </p>
              </div>

              {/* Merged Analysis Results */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="font-semibold text-medical-900 mb-4 flex items-center space-x-2">
                  <FileImage className="w-5 h-5" />
                  <span>Combined Analysis Results</span>
                </h3>
                
                {/* Collect all unique diagnoses */}
                {(() => {
                  const allDiagnoses: any[] = [];
                  const seenFields = new Set<string>();
                  
                  // Collect all diagnoses from all results
                  analysisResults.forEach(result => {
                    result.diagnoses.forEach((diagnosis: any) => {
                      // Only add if we haven't seen this field yet
                      if (!seenFields.has(diagnosis.field_name)) {
                        seenFields.add(diagnosis.field_name);
                        allDiagnoses.push(diagnosis);
                      }
                    });
                  });
                  
                  return allDiagnoses.length > 0 ? (
                    <div className="space-y-3">
                      {allDiagnoses.map((diagnosis: any, diagIndex: number) => (
                        <div
                          key={diagIndex}
                          className="bg-medical-50 rounded-lg p-4 border border-medical-200"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="text-sm font-semibold text-medical-900">
                                {formatFieldName(diagnosis.field_name)}
                              </p>
                              <p className={`text-lg font-medium mt-1 ${
                                diagnosis.field_value.toLowerCase() === 'yes' 
                                  ? 'text-green-600' 
                                  : diagnosis.field_value.toLowerCase() === 'no'
                                  ? 'text-gray-500'
                                  : 'text-primary-600'
                              }`}>
                                {formatFieldValue(diagnosis.field_value)}
                              </p>
                            </div>
                            <div className="ml-4">
                              {diagnosis.field_value.toLowerCase() === 'yes' && (
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                </div>
                              )}
                              {diagnosis.field_value.toLowerCase() === 'no' && (
                                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                  <X className="w-5 h-5 text-gray-500" />
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-medical-600 italic">
                            {getFieldExplanation(diagnosis.field_name, diagnosis.field_value)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-medical-600 italic">No clinical findings detected.</p>
                  );
                })()}
              </div>

              {/* Image Display Section */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-medical-900 mb-4">Analyzed Images</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                  {previews.map((preview, index) => (
                    <div key={index} className="relative">
                      <img
                        src={preview}
                        alt={`Analyzed scan ${index + 1}`}
                        className="w-full h-40 object-cover rounded-lg border-2 border-primary-500"
                      />
                      <div className="absolute bottom-2 left-2 right-2 bg-white bg-opacity-90 rounded px-2 py-1">
                        <p className="text-xs truncate">{selectedFiles[index].name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-center space-x-4 pt-4">
                <button
                  onClick={() => {
                    setShowUserInfoModal(true);
                  }}
                  className="medical-button-secondary flex items-center space-x-2"
                >
                  <Mail className="w-4 h-4" />
                  <span>Email Report</span>
                </button>
                
                <button
                  onClick={async () => {
                    // If user info not collected yet, show modal
                    if (!userInfo) {
                      setShowUserInfoModal(true);
                    } else {
                      // Collect all unique diagnoses
                      const allDiagnoses: any[] = [];
                      const seenFields = new Set<string>();
                      
                      analysisResults.forEach(result => {
                        result.diagnoses.forEach((diagnosis: any) => {
                          if (!seenFields.has(diagnosis.field_name)) {
                            seenFields.add(diagnosis.field_name);
                            allDiagnoses.push(diagnosis);
                          }
                        });
                      });
                      
                      // Generate PDF blob for server upload
                      const pdfBlob = await generateScanAnalysisPDF(allDiagnoses, previews, selectedFiles, userInfo, true) as Blob;
                      
                      // Upload to server
                      try {
                        const dateStr = new Date().toISOString().split('T')[0];
                        const patientName = userInfo.fullName ? `-${userInfo.fullName.replace(/\s+/g, '-')}` : '';
                        const filename = `OrthoMind-smart-scan-analysis${patientName}-${dateStr}.pdf`;
                        
                        await apiService.uploadPDF(pdfBlob, filename);
                        console.log('PDF uploaded to server successfully');
                      } catch (error) {
                        console.error('Failed to upload PDF:', error);
                      }
                      
                      // Also download to user's device
                      await generateScanAnalysisPDF(allDiagnoses, previews, selectedFiles, userInfo);
                    }
                  }}
                  className="medical-button flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Download PDF Report</span>
                </button>
                
                <button
                  onClick={clearAll}
                  className="medical-button"
                >
                  Analyze New Scans
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* User Info Modal */}
      <UserInfoModal
        isOpen={showUserInfoModal}
        onClose={() => setShowUserInfoModal(false)}
        onSubmit={handleUserInfoSubmit}
        onSkip={async () => {
          // Generate PDF without user info
          const allDiagnoses: any[] = [];
          const seenFields = new Set<string>();
          
          analysisResults.forEach(result => {
            result.diagnoses.forEach((diagnosis: any) => {
              if (!seenFields.has(diagnosis.field_name)) {
                seenFields.add(diagnosis.field_name);
                allDiagnoses.push(diagnosis);
              }
            });
          });
          
          // Generate PDF blob for server upload (anonymous)
          const pdfBlob = await generateScanAnalysisPDF(allDiagnoses, previews, selectedFiles, undefined, true) as Blob;
          
          // Upload anonymous PDF to server
          try {
            const dateStr = new Date().toISOString().split('T')[0];
            const filename = `OrthoMind-smart-scan-analysis-anonymous-${dateStr}.pdf`;
            
            await apiService.uploadPDF(pdfBlob, filename);
            console.log('Anonymous PDF uploaded to server successfully');
          } catch (error) {
            console.error('Failed to upload anonymous PDF:', error);
          }
          
          // Also download to user's device
          await generateScanAnalysisPDF(allDiagnoses, previews, selectedFiles, undefined);
        }}
        isLoading={isAnalyzing}
      />
    </div>
  );
};

export default ScanAnalysisPage;